import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  CircularProgress,
  Chip,
  MenuItem,
  InputLabel,
  TextField,
  Autocomplete,
  FormControl,
  FormControlLabel,
  Switch,
  Select,
  Stack,
} from "@mui/material";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";

import { GiSpades } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import config from "../config";
import Pagination from "../components/Pagination";
import { red } from "@mui/material/colors";

const baseUrl = config.baseURL;
const sortFields = [
  { label: "Created At", value: "a.created_at" },
  { label: "Start Date", value: "a.start_date" },
  { label: "End Date", value: "a.end_date" },
  { label: "Bid Price", value: "a.bid_price" },
  { label: "Max Budget", value: "a.max_budget" },
  { label: "Impressions", value: "a.impressions" },
  { label: "Clicks", value: "a.clicks" },
  { label: "Title", value: "a.title" },
  { label: "Status", value: "a.status" },
];
const AdsManager = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);
  const [advertiserId, setAdvertiserId] = useState(null);
  const [filtersInput, setFiltersInput] = useState({
    title: "",
    call_to_action: "",
    start_date: "",
    end_date: "",
    created_at: "",
    tags: [],
    targeting_industries: [],
    targeting_categories: [],
  });

  const [filtersApplied, setFiltersApplied] = useState({});
  const [selectedField, setSelectedField] = useState("");
  const [updatingAdId, setUpdatingAdId] = useState(null);
  const [sort, setSort] = useState({ field: "a.created_at", order: "desc" });

  // Pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemPerPage = 10;

  const token = localStorage.getItem("token");
  const buildParams = (filtersData, page = 1, sortData = sort) => {
    const params = {
      page,
      limit: itemPerPage,
      sortField: sortData.field,
      sortOrder: sortData.order,
    };

    Object.entries(filtersData).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length) {
          params[key] = JSON.stringify(value); // backend expects ["shipping"]
        } else {
          params[key] = value;
        }
      }
    });

    return params;
  };

  // Decode JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      setUserData(decoded);
      setAdvertiserId(decoded.advertiserId || null);
    } catch (err) {
      console.error("Invalid token", err);
    }
  }, [token]);

  // Fetch ads function (now at component level)
  const fetchAds = async (
    page = 1,
    filtersData = filtersApplied,
    sortData = sort
  ) => {
    setLoading(true);
    try {
      if (!token || !advertiserId) return;

      const params = {
        page,
        limit: 10,
        sortField: sort.field,
        sortOrder: sort.order,
      };

      // Ensure every filter is serialized correctly
      Object.entries(filtersData).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          if (Array.isArray(value) || typeof value === "object") {
            params[key] = JSON.stringify(value); // stringify arrays/objects
          } else {
            params[key] = value; // leave primitives as-is
          }
        }
      });

      console.log("Axios params:", params);
      console.log("filtersData", filtersData);

      // Build full URL manually for logging

      const query = new URLSearchParams(params).toString();
      console.log("Full URL sent to backend:", `${baseUrl}/api/ads?${query}`);

      const res = await axios.get(`${baseUrl}/api/ads?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const filteredAds = Array.isArray(res.data.data) ? res.data.data : [];
      const totalFilteredItems =
        res.data.pagination?.totalItems || filteredAds.length;
      const currentPageFromServer = res.data.pagination?.currentPage || page;
      console.log("data res ads ", res);
      setAds(filteredAds);
      setTotalItems(totalFilteredItems);
      setCurrentPage(currentPageFromServer);
    } catch (err) {
      console.error("Error fetching ads:", err);
      setError("Failed to fetch ads.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch ads when advertiserId changes
  useEffect(() => {
    if (advertiserId) {
      fetchAds(currentPage, filtersApplied, sort);
    }
    // eslint-disable-next-line
  }, [advertiserId, currentPage, filtersApplied, sort]);

  // Pagination handler
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAds(page);
  };

  // Navigation handlers

  const handleAdDashboard = () => navigate("/dashboard/AdvertiserDashboard");
  const handleCreateAd = () => navigate("/dashboard/Advertiser/create-ad");
  const handleEditAd = (adId) =>
    navigate(`/dashboard/Advertiser/edit-ad/${adId}`);
  const handleViewDetails = (adId) =>
    navigate(`/dashboard/Advertiser/AdDetails/${adId}`);
  const applyFilter = () => {
    const newFilters = {};

    Object.entries(filtersInput).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        newFilters[key] = value; // keep array; buildParams will JSON.stringify
      } else if (typeof value === "string" && value.trim() !== "") {
        newFilters[key] = value.trim();
      }
    });

    console.log("applyFilter -> filtersInput:", filtersInput);
    console.log("applyFilter -> newFilters (to be applied):", newFilters);

    setFiltersApplied(newFilters);
    setCurrentPage(1);

    // fetch immediately and log the decoded URL (readable)
    const params = buildParams(newFilters, 1, sort);
    const qs = new URLSearchParams(params).toString();
    console.log(
      "Full URL sent to backend (decoded):",
      decodeURIComponent(`${baseUrl}/api/ads?${qs}`)
    );

    fetchAds(1, newFilters, sort);
  };

  if (!advertiserId) {
    return (
      <Box sx={{ mt: 10, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          You must be an advertiser to manage ads.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, fontFamily: "Poppins, sans-serif" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 3,
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <GiSpades color="#EA6A12" size={30} /> Your Ads
        </Typography>
        <Stack direction="column" spacing={2}>
          <Button
            variant="contained"
            startIcon={<GiSpades />}
            onClick={handleCreateAd}
            sx={{
              backgroundColor: "#EA6A12",
              "&:hover": { backgroundColor: "#c7570f" },
              fontWeight: "bold",
              textTransform: "none",
            }}
          >
            Create New Ad
          </Button>

          <Button
            variant="contained"
            startIcon={<GiSpades />}
            onClick={handleAdDashboard}
            sx={{
              backgroundColor: "#EA6A12",
              "&:hover": { backgroundColor: "#c7570f" },
              fontWeight: "bold",
              textTransform: "none",
            }}
          >
            Ads Dashboard
          </Button>
        </Stack>
      </Box>
      {/* Filters */}
      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          alignItems: "center",
        }}
      >
        {/* Field selector */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter Field</InputLabel>
          <Select
            value={selectedField}
            label="Filter Field"
            onChange={(e) => setSelectedField(e.target.value)}
          >
            <MenuItem value="title">Title</MenuItem>
            <MenuItem value="call_to_action">Call to Action</MenuItem>
            <MenuItem value="date">Date</MenuItem>
            <MenuItem value="created_at">Created At</MenuItem>
            <MenuItem value="tags">Tags</MenuItem>
            <MenuItem value="targeting_industries">Industries</MenuItem>
            <MenuItem value="targeting_categories">Categories</MenuItem>
          </Select>
        </FormControl>

        {/* Dynamic Filter Input */}
        <Box
          sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 250 }}
        >
          {/* Text filters */}
          {[
            "title",
            "call_to_action",
            "tags",
            "targeting_industries",
            "targeting_categories",
          ].includes(selectedField) && (
            <TextField
              label={selectedField.replace("_", " ").toUpperCase()}
              size="small"
              value={filtersInput[selectedField]} // fallback to empty string
              onChange={(e) =>
                setFiltersInput({
                  ...filtersInput,
                  [selectedField]: e.target.value,
                })
              }
            />
          )}

          {/* Date range (start_date + end_date) */}
          {selectedField === "date" && (
            <>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={filtersInput.start_date || ""}
                onChange={(e) =>
                  setFiltersInput((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={filtersInput.end_date || ""}
                onChange={(e) =>
                  setFiltersInput((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          {/* Single created_at date */}
          {selectedField === "created_at" && (
            <TextField
              label="Created At"
              type="date"
              size="small"
              value={filtersInput.created_at || ""}
              onChange={(e) =>
                setFiltersInput((prev) => ({
                  ...prev,
                  created_at: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
          )}
        </Box>

        {/* Apply Filter - CALL THE applyFilter FUNCTION */}
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#EA6A12",
            "&:hover": { backgroundColor: "#c7570f" },
            height: 40,
          }}
          onClick={applyFilter} // <<< important: call applyFilter
          disabled={!selectedField} // optional: prevent clicking with no selected field
        >
          Apply Filter
        </Button>

        {/* Sort dropdowns */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sort.field}
            label="Sort By"
            onChange={(e) => setSort({ ...sort, field: e.target.value })}
          >
            {sortFields.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 120, display: "flex", alignItems: "center" }}
        >
          <InputLabel>Order</InputLabel>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Select
              value={sort.order}
              label="Order"
              onChange={(e) => setSort({ ...sort, order: e.target.value })}
              sx={{ minWidth: 90 }}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
            {sort.order === "asc" ? (
              <ArrowUpward fontSize="small" />
            ) : (
              <ArrowDownward fontSize="small" />
            )}
          </Box>
        </FormControl>
      </Box>

      {/* Loading/Error */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : ads.length === 0 ? (
        <Typography>No ads created yet.</Typography>
      ) : (
        <Grid container spacing={3}>
          {ads?.map((ad) => {
            const isExpired = ad.end_date && new Date(ad.end_date) < new Date();

            return (
              <Grid item xs={12} sm={6} md={4} key={ad.id}>
                <Card
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    cursor: "pointer",
                  }}
                >
                  <CardContent onClick={() => handleViewDetails(ad.id)}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                      {ad.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {ad.description?.slice(0, 80)}...
                    </Typography>

                    {/* Key Info */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      {(() => {
                        let tags = ad.tags;

                        if (typeof tags === "string") {
                          try {
                            tags = JSON.parse(tags);
                          } catch (e) {
                            tags = [];
                          }
                        }

                        return Array.isArray(tags)
                          ? tags.map((tag, idx) => (
                              <Chip
                                key={idx}
                                label={tag}
                                size="small"
                                sx={{ backgroundColor: "#F0F4FF" }}
                              />
                            ))
                          : null;
                      })()}
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Status: {ad.status} | Budget: ${ad.max_budget} | Bid: $
                      {ad.bid_price}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ justifyContent: "flex-end" }}>
                    <Button size="small" onClick={() => handleEditAd(ad.id)}>
                      Edit
                    </Button>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={ad.status === "active"}
                          onChange={async (e) => {
                            const newStatus = e.target.checked
                              ? "active"
                              : "paused";
                            setUpdatingAdId(ad.id);
                            try {
                              const res = await axios.put(
                                `${baseUrl}/api/ads/status/${ad.id}`,
                                { status: newStatus },
                                {
                                  headers: { Authorization: `Bearer ${token}` },
                                }
                              );
                              console.log("res switch", res.data.message);
                              setAds((prev) =>
                                prev.map((a) =>
                                  a.id === ad.id
                                    ? { ...a, status: newStatus }
                                    : a
                                )
                              );
                            } catch (err) {
                              console.error(err);
                              alert(
                                err.response?.data?.message ||
                                  "Failed to update status"
                              );
                            } finally {
                              setUpdatingAdId(null);
                            }
                          }}
                          color="primary"
                          disabled={isExpired || updatingAdId === ad.id}
                        />
                      }
                      label={
                        isExpired ? (
                          <span style={{ color: red[500], fontWeight: "bold" }}>
                            Expired
                          </span>
                        ) : ad.status === "active" ? (
                          "Active"
                        ) : (
                          "Paused"
                        )
                      }
                    />
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      {/* Pagination */}
      {totalItems > itemPerPage && (
        <Pagination
          totalItem={totalItems}
          itemPerPage={itemPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          fetchItems={fetchAds}
        />
      )}
    </Box>
  );
};

export default AdsManager;
